import { create } from 'zustand';

import {
  DOCK_DEFAULT_WIDTH,
  DOCK_MIN_WIDTH,
  DOCK_MAX_WIDTH,
  expandedStorageKey,
  openStorageKey,
  widthStorageKey,
} from './chatDockConstants';

interface ChatDockState {
  open: boolean;
  /**
   * Collapse-aware open: true while the dock is open AND throughout the closing
   * collapse animation (`open || collapsing`). Published by `ChatDockColumn` (the
   * sole writer), it mirrors the value driving the dock's body/portal-target
   * visibility. `<DockSlot>` gates a default ("floor") child on THIS — not raw
   * `open` — so the floor body stays mounted through the close animation and
   * unmounts only after it settles, matching the dock's own target div.
   */
  bodyOpen: boolean;
  width: number;
  /** Whether the dock is in its expanded (overlay) presentation. */
  expanded: boolean;
  /** persistKey whose storage the setters write to. */
  activePersistKey: string | null;
  /** The dock's portal target element for an `external` surface (set by the dock
   *  frame, consumed by `<DockSlot>` to portal its body in). Null when no external
   *  surface is mounted. */
  portalTarget: HTMLElement | null;
  /** Publish (or clear) the portal target element. */
  setPortalTarget: (el: HTMLElement | null) => void;
  /** Publish the collapse-aware open value (see `bodyOpen`). Written by the dock column. */
  setBodyOpen: (bodyOpen: boolean) => void;
  /** Load open/width/expanded from storage for `persistKey`, falling back to defaultOpen / default width. */
  hydrate: (persistKey: string, defaultOpen: boolean) => void;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  expand: () => void;
  setWidth: (width: number) => void;
  setExpanded: (expanded: boolean) => void;
  toggleExpanded: () => void;
}

const clampWidth = (w: number) => Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, w));

const parseStoredWidth = (s: string): number => {
  const n = Number(s);
  return Number.isNaN(n) ? DOCK_DEFAULT_WIDTH : clampWidth(n);
};

/** Global store for the ChatDock's open state and width, with localStorage persistence per persistKey. */
export const useChatDockStore = create<ChatDockState>((set, get) => ({
  open: false,
  bodyOpen: false,
  width: DOCK_DEFAULT_WIDTH,
  expanded: false,
  activePersistKey: null,
  portalTarget: null,
  setPortalTarget: (el) => set({ portalTarget: el }),
  setBodyOpen: (bodyOpen) => set({ bodyOpen }),
  hydrate: (persistKey, defaultOpen) => {
    const storedOpen = localStorage.getItem(openStorageKey(persistKey));
    const storedWidth = localStorage.getItem(widthStorageKey(persistKey));
    const storedExpanded = localStorage.getItem(expandedStorageKey(persistKey));
    set({
      activePersistKey: persistKey,
      open: storedOpen === null ? defaultOpen : storedOpen === 'true',
      width: storedWidth === null ? DOCK_DEFAULT_WIDTH : parseStoredWidth(storedWidth),
      expanded: storedExpanded === 'true',
    });
  },
  setOpen: (open) => {
    const key = get().activePersistKey;
    if (key) localStorage.setItem(openStorageKey(key), String(open));
    set({ open });
  },
  toggle: () => get().setOpen(!get().open),
  expand: () => {
    if (!get().open) get().setOpen(true);
  },
  setWidth: (width) => {
    const w = clampWidth(width);
    const key = get().activePersistKey;
    if (key) localStorage.setItem(widthStorageKey(key), String(w));
    set({ width: w });
  },
  setExpanded: (expanded) => {
    const key = get().activePersistKey;
    if (key) localStorage.setItem(expandedStorageKey(key), String(expanded));
    set({ expanded });
  },
  toggleExpanded: () => get().setExpanded(!get().expanded),
}));
